import type {
  ExperienceGridItemProps,
  ExperienceGridProps,
  ExperienceStackProps,
  ExperienceSurfaceProps,
} from '../catalog/experience-layout-definitions'

const GRID_COMPACT_COLUMN_CLASSES = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
  6: 'grid-cols-6',
  12: 'grid-cols-12',
} as const

const GRID_WEB_COLUMN_CLASSES = {
  1: 'md:grid-cols-1',
  2: 'md:grid-cols-2',
  3: 'md:grid-cols-3',
  4: 'md:grid-cols-4',
  6: 'md:grid-cols-6',
  12: 'md:grid-cols-12',
} as const

const GRID_ITEM_COMPACT_SPAN_CLASSES = {
  1: 'col-span-1',
  2: 'col-span-2',
  3: 'col-span-3',
  4: 'col-span-4',
} as const

const GRID_ITEM_WEB_SPAN_CLASSES = {
  1: 'md:col-span-1',
  2: 'md:col-span-2',
  3: 'md:col-span-3',
  4: 'md:col-span-4',
  6: 'md:col-span-6',
  8: 'md:col-span-8',
  12: 'md:col-span-12',
} as const

const ORDER_COMPACT_CLASSES = {
  0: 'order-0',
  1: 'order-1',
  2: 'order-2',
  3: 'order-3',
  4: 'order-4',
  5: 'order-5',
  6: 'order-6',
  7: 'order-7',
  8: 'order-8',
  9: 'order-9',
  10: 'order-10',
  11: 'order-11',
  12: 'order-12',
} as const

const ORDER_WEB_CLASSES = {
  0: 'md:order-0',
  1: 'md:order-1',
  2: 'md:order-2',
  3: 'md:order-3',
  4: 'md:order-4',
  5: 'md:order-5',
  6: 'md:order-6',
  7: 'md:order-7',
  8: 'md:order-8',
  9: 'md:order-9',
  10: 'md:order-10',
  11: 'md:order-11',
  12: 'md:order-12',
} as const

const GAP_CLASSES = {
  none: 'gap-0',
  sm: 'gap-2',
  md: 'gap-4',
  lg: 'gap-6',
} as const

const STACK_DIRECTION_CLASSES = {
  horizontal: 'flex-row',
  vertical: 'flex-col',
} as const

const STACK_ALIGNMENT_CLASSES = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
  stretch: 'items-stretch',
} as const

const SURFACE_WIDTH_CLASSES = { full: 'w-full' } as const
const SURFACE_MAX_WIDTH_COMPACT_CLASSES = { full: 'max-w-full', '6xl': 'max-w-6xl' } as const
const SURFACE_MAX_WIDTH_WEB_CLASSES = { full: 'md:max-w-full', '6xl': 'md:max-w-6xl' } as const
const SURFACE_CENTERING_COMPACT_CLASSES = { false: '', true: 'mx-auto' } as const
const SURFACE_CENTERING_WEB_CLASSES = { false: 'md:mx-0', true: 'md:mx-auto' } as const
const SURFACE_PADDING_COMPACT_CLASSES = { none: '', sm: 'px-2', md: 'px-4' } as const
const SURFACE_PADDING_WEB_CLASSES = { none: 'md:px-0', sm: 'md:px-2', md: 'md:px-4' } as const
const SURFACE_PADDING_TOP_COMPACT_CLASSES = { none: '', md: 'pt-4' } as const
const SURFACE_PADDING_TOP_WEB_CLASSES = { none: 'md:pt-0', md: 'md:pt-4' } as const
const SURFACE_PADDING_BOTTOM_COMPACT_CLASSES = { none: '', md: 'pb-4', lg: 'pb-8' } as const
const SURFACE_PADDING_BOTTOM_WEB_CLASSES = { none: 'md:pb-0', md: 'md:pb-4', lg: 'md:pb-8' } as const
const SURFACE_MARGIN_BOTTOM_COMPACT_CLASSES = { none: '', md: 'mb-4' } as const
const SURFACE_MARGIN_BOTTOM_WEB_CLASSES = { none: 'md:mb-0', md: 'md:mb-4' } as const
const SURFACE_RADIUS_COMPACT_CLASSES = { none: '', lg: 'rounded-lg' } as const
const SURFACE_RADIUS_WEB_CLASSES = { none: 'md:rounded-none', lg: 'md:rounded-lg' } as const
const SURFACE_BACKGROUND_COMPACT_CLASSES = { none: '', page: 'bg-background' } as const
const SURFACE_BACKGROUND_WEB_CLASSES = { none: 'md:bg-transparent', page: 'md:bg-background' } as const

const surfaceLayoutClasses = (props: ExperienceSurfaceProps): readonly string[] => {
  if (props.compact.layout === props.web.layout) return props.compact.layout === 'course-grid' ? ['courses-grid'] : []

  if (props.compact.layout === 'course-grid') return ['courses-grid', 'md:block', 'md:gap-0', 'md:py-0']

  return ['md:grid', 'md:[grid-template-columns:repeat(auto-fill,minmax(300px,1fr))]', 'md:gap-6', 'md:py-4']
}

/** Complete literal utilities emitted by the shared native layout components. */
export const EXPERIENCE_LAYOUT_TAILWIND_SAFELIST = Object.freeze(
  [
    'grid',
    'contents',
    'flex',
    'mx-auto',
    'md:mx-0',
    'md:mx-auto',
    'courses-grid',
    'md:block',
    'md:gap-0',
    'md:py-0',
    'md:grid',
    'md:[grid-template-columns:repeat(auto-fill,minmax(300px,1fr))]',
    'md:gap-6',
    'md:py-4',
    ...Object.values(GRID_COMPACT_COLUMN_CLASSES),
    ...Object.values(GRID_WEB_COLUMN_CLASSES),
    ...Object.values(GRID_ITEM_COMPACT_SPAN_CLASSES),
    ...Object.values(GRID_ITEM_WEB_SPAN_CLASSES),
    ...Object.values(ORDER_COMPACT_CLASSES),
    ...Object.values(ORDER_WEB_CLASSES),
    ...Object.values(GAP_CLASSES),
    ...Object.values(STACK_DIRECTION_CLASSES),
    ...Object.values(STACK_ALIGNMENT_CLASSES),
    ...Object.values(SURFACE_WIDTH_CLASSES),
    ...Object.values(SURFACE_MAX_WIDTH_COMPACT_CLASSES),
    ...Object.values(SURFACE_MAX_WIDTH_WEB_CLASSES),
    ...Object.values(SURFACE_CENTERING_COMPACT_CLASSES),
    ...Object.values(SURFACE_CENTERING_WEB_CLASSES),
    ...Object.values(SURFACE_PADDING_COMPACT_CLASSES),
    ...Object.values(SURFACE_PADDING_WEB_CLASSES),
    ...Object.values(SURFACE_PADDING_TOP_COMPACT_CLASSES),
    ...Object.values(SURFACE_PADDING_TOP_WEB_CLASSES),
    ...Object.values(SURFACE_PADDING_BOTTOM_COMPACT_CLASSES),
    ...Object.values(SURFACE_PADDING_BOTTOM_WEB_CLASSES),
    ...Object.values(SURFACE_MARGIN_BOTTOM_COMPACT_CLASSES),
    ...Object.values(SURFACE_MARGIN_BOTTOM_WEB_CLASSES),
    ...Object.values(SURFACE_RADIUS_COMPACT_CLASSES),
    ...Object.values(SURFACE_RADIUS_WEB_CLASSES),
    ...Object.values(SURFACE_BACKGROUND_COMPACT_CLASSES),
    ...Object.values(SURFACE_BACKGROUND_WEB_CLASSES),
  ].filter(Boolean)
)

export const experienceLayoutClasses = {
  grid: (props: ExperienceGridProps) => [
    'grid',
    GRID_COMPACT_COLUMN_CLASSES[props.columns.compact],
    GRID_WEB_COLUMN_CLASSES[props.columns.web],
    GAP_CLASSES[props.gap],
  ],
  gridItem: (props: ExperienceGridItemProps) => [
    props.wrapper === 'contents' ? 'contents' : '',
    GRID_ITEM_COMPACT_SPAN_CLASSES[props.span.compact],
    GRID_ITEM_WEB_SPAN_CLASSES[props.span.web],
    props.order === undefined ? '' : ORDER_COMPACT_CLASSES[props.order.compact],
    props.order === undefined ? '' : ORDER_WEB_CLASSES[props.order.web],
  ],
  stack: (props: ExperienceStackProps) => [
    'flex',
    STACK_DIRECTION_CLASSES[props.direction],
    GAP_CLASSES[props.gap],
    props.align ? STACK_ALIGNMENT_CLASSES[props.align] : '',
  ],
  surface: (props: ExperienceSurfaceProps) => [
    ...surfaceLayoutClasses(props),
    SURFACE_WIDTH_CLASSES[props.compact.width],
    SURFACE_MAX_WIDTH_COMPACT_CLASSES[props.compact.maxWidth],
    SURFACE_CENTERING_COMPACT_CLASSES[String(props.compact.centered) as 'false' | 'true'],
    SURFACE_PADDING_COMPACT_CLASSES[props.compact.padding],
    SURFACE_PADDING_TOP_COMPACT_CLASSES[props.compact.paddingTop],
    SURFACE_PADDING_BOTTOM_COMPACT_CLASSES[props.compact.paddingBottom],
    SURFACE_MARGIN_BOTTOM_COMPACT_CLASSES[props.compact.marginBottom],
    SURFACE_RADIUS_COMPACT_CLASSES[props.compact.radius],
    SURFACE_BACKGROUND_COMPACT_CLASSES[props.compact.background],
    SURFACE_MAX_WIDTH_WEB_CLASSES[props.web.maxWidth],
    SURFACE_CENTERING_WEB_CLASSES[String(props.web.centered) as 'false' | 'true'],
    SURFACE_PADDING_WEB_CLASSES[props.web.padding],
    SURFACE_PADDING_TOP_WEB_CLASSES[props.web.paddingTop],
    SURFACE_PADDING_BOTTOM_WEB_CLASSES[props.web.paddingBottom],
    SURFACE_MARGIN_BOTTOM_WEB_CLASSES[props.web.marginBottom],
    SURFACE_RADIUS_WEB_CLASSES[props.web.radius],
    SURFACE_BACKGROUND_WEB_CLASSES[props.web.background],
  ],
} as const
